// Hedefler tablosu (TODO-KPI-01): set yaprak göstergeleri × hedef satırları; satır içi
// düzenleyici; "hedefe uzaklık" + ilerleme çubuğu. Tüm sayılar sunucudan (progress DTO).
import { Fragment, useMemo, useState } from "react";
import type {
  ClientEntity,
  KpiResolvedIndicator,
  KpiTargetUpsert,
  KpiTargetWithProgress,
} from "@mahir/shared";
import { KPI_TARGET_STATUS_LABELS, KPI_TARGET_TYPE_LABELS } from "@mahir/shared";
import {
  draftFromTarget,
  emptyDraft,
  formatDistance,
  formatTargetCell,
  formatTargetNumber,
  KPI_TARGETS_COPY,
  targetTone,
  targetToneColor,
} from "../../../lib/kpi-targets-ui";
import { useMediaQuery } from "../../../hooks/use-media-query";
import { useTreeCollapsed } from "../../../hooks/use-tree-collapsed";
import { buildTreeList, treeIndent, type TreeListRow } from "../../../lib/kpi-tree-list";
import { TreeLabelStack } from "./TreeLabelStack";
import { KpiTreeBandRow, KpiTreeToolbar } from "../KpiTreeBandRow";
import { ConfirmDialog } from "../../common/ConfirmDialog";
import { TargetEditor } from "./TargetEditor";
import { TargetProgressBar } from "./TargetProgressBar";

const C = KPI_TARGETS_COPY.targets;

const th: React.CSSProperties = {
  padding: "8px 10px",
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-3)",
  borderBottom: "1px solid var(--color-border)",
  textAlign: "left",
  whiteSpace: "nowrap",
};
const td: React.CSSProperties = {
  padding: "8px 10px",
  borderBottom: "1px solid var(--color-border)",
  fontSize: "0.85rem",
  color: "var(--color-text)",
  verticalAlign: "top",
  fontVariantNumeric: "tabular-nums",
};
const link: React.CSSProperties = {
  color: "var(--color-primary)",
  fontSize: "0.78rem",
  minHeight: 28,
};

export interface TargetsTableProps {
  indicators: KpiResolvedIndicator[];
  /** T7 — kategori bantları için tüm set göstergeleri (verilmezse düz liste) */
  treeIndicators?: KpiResolvedIndicator[];
  initialCollapsed?: string[];
  targets: KpiTargetWithProgress[];
  entities: ClientEntity[];
  canEdit: boolean;
  busy: boolean;
  error: string | null;
  onSave: (body: KpiTargetUpsert) => Promise<boolean>;
  onDelete: (targetId: string) => Promise<void>;
  onOpen: (code: string) => void;
}

export function TargetsTable(props: TargetsTableProps): JSX.Element {
  const { indicators, targets, entities, canEdit, busy, error, onSave, onDelete, onOpen } = props;
  // editing: { metricId, targetId? } — tek düzenleyici açık
  const [editing, setEditing] = useState<{
    metricId: string;
    target?: KpiTargetWithProgress;
  } | null>(null);
  const [confirm, setConfirm] = useState<KpiTargetWithProgress | null>(null);
  const tc = useTreeCollapsed("hedefler", props.treeIndicators ?? [], props.initialCollapsed);
  const narrow = useMediaQuery("(max-width: 1024px)");
  const list = useMemo<TreeListRow<KpiResolvedIndicator>[]>(
    () =>
      props.treeIndicators
        ? buildTreeList(
            props.treeIndicators,
            indicators.map((i) => ({ indicatorId: i.id, matches: true, data: i })),
            { collapsed: tc.collapsed, filtered: false },
          )
        : indicators.map((i) => ({ kind: "item", data: i, indicatorId: i.id, depth: 0 })),
    [props.treeIndicators, indicators, tc.collapsed],
  );
  const byMetric = new Map<string, KpiTargetWithProgress[]>();
  for (const t of targets) byMetric.set(t.metricId, [...(byMetric.get(t.metricId) ?? []), t]);

  const editorRow = (ind: KpiResolvedIndicator, target?: KpiTargetWithProgress): JSX.Element => (
    <tr key={`${ind.id}-editor`}>
      <td colSpan={8} style={{ ...td, padding: 10 }}>
        <TargetEditor
          initial={target ? draftFromTarget(target) : emptyDraft(ind.metricId)}
          entities={entities}
          unit={ind.unit}
          busy={busy}
          error={error}
          onSave={(body) => {
            void onSave(body).then((ok) => {
              if (ok) setEditing(null);
            });
          }}
          onCancel={() => setEditing(null)}
        />
      </td>
    </tr>
  );

  return (
    <div className="kpi-scroll-x">
      {list.some((r) => r.kind === "band") ? (
        <KpiTreeToolbar onCollapseAll={tc.collapseAll} onExpandAll={tc.expandAll} />
      ) : null}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={th}>{C.columns.indicator}</th>
            <th style={th}>{C.columns.target}</th>
            <th style={{ ...th, textAlign: "right" }}>{C.columns.current}</th>
            <th style={{ ...th, textAlign: "right" }}>{C.columns.distance}</th>
            <th style={th}>{C.columns.progress}</th>
            <th style={th}>{C.columns.owner}</th>
            <th style={th}>{C.columns.status}</th>
            <th style={th}>{C.columns.actions}</th>
          </tr>
        </thead>
        <tbody>
          {indicators.length === 0 ? (
            <tr>
              <td
                colSpan={8}
                style={{ ...td, textAlign: "center", color: "var(--color-text-3)", padding: 28 }}
              >
                {C.empty}
              </td>
            </tr>
          ) : null}
          {list.map((entry) => {
            if (entry.kind === "band") {
              return (
                <KpiTreeBandRow
                  key={`band-${entry.indicator.id}`}
                  row={entry}
                  colSpan={8}
                  narrow={narrow}
                  filtered={false}
                  onToggle={tc.setBand}
                />
              );
            }
            const ind = entry.data;
            const indent = treeIndent(entry.depth, narrow);
            const rows = byMetric.get(ind.metricId) ?? [];
            const isPct = ind.valueType === "percent";
            const addBtn =
              canEdit && !(editing?.metricId === ind.metricId && !editing.target) ? (
                <button
                  type="button"
                  style={link}
                  onClick={() => setEditing({ metricId: ind.metricId })}
                >
                  {C.addTarget}
                </button>
              ) : null;
            return (
              <Fragment key={ind.id}>
                {rows.length === 0 ? (
                  <tr data-metric={ind.code}>
                    <td style={{ ...td, textAlign: "left" }}>
                      <TreeLabelStack
                        label={ind.label}
                        indent={indent}
                        onOpen={() => onOpen(ind.code)}
                        title="Veri Girişi'nde aç"
                        sub={ind.unit}
                      />
                    </td>
                    <td style={{ ...td, color: "var(--color-text-3)" }}>{C.noTarget}</td>
                    <td style={{ ...td, textAlign: "right", color: "var(--color-text-3)" }}>—</td>
                    <td style={{ ...td, textAlign: "right", color: "var(--color-text-3)" }}>—</td>
                    <td style={td} />
                    <td style={td} />
                    <td style={td} />
                    <td style={td}>{addBtn}</td>
                  </tr>
                ) : (
                  rows.map((t, idx) => {
                    const tone = targetTone(t.progress);
                    const isEditing = editing?.target?.id === t.id;
                    const scopeLine = t.entityName ? (
                      <div className="text-xs" style={{ color: "var(--color-text-2)" }}>
                        {C.perEntity}: {t.entityName}
                      </div>
                    ) : rows.length > 1 ? (
                      <div className="text-xs" style={{ color: "var(--color-text-2)" }}>
                        {C.consolidated}
                      </div>
                    ) : null;
                    return (
                      <Fragment key={t.id}>
                        <tr data-metric={ind.code} data-target={t.id}>
                          <td style={{ ...td, textAlign: "left" }}>
                            {idx === 0 ? (
                              <TreeLabelStack
                                label={ind.label}
                                indent={indent}
                                onOpen={() => onOpen(ind.code)}
                                title="Veri Girişi'nde aç"
                                sub={ind.unit}
                              >
                                {scopeLine}
                              </TreeLabelStack>
                            ) : (
                              <div style={{ marginLeft: indent, textAlign: "left" }}>
                                {scopeLine}
                              </div>
                            )}
                          </td>
                          <td style={td}>
                            <div>{formatTargetCell(t)}</div>
                            <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
                              {KPI_TARGET_TYPE_LABELS[t.targetType]}
                              {t.baselineYear
                                ? ` · ${C.baselineFrom(t.baselineYear)}${t.progress.baselineValue !== null ? ` = ${formatTargetNumber(t.progress.baselineValue, { unit: t.unit, percent: isPct })}` : ""}`
                                : ""}
                            </div>
                          </td>
                          <td style={{ ...td, textAlign: "right" }}>
                            {t.progress.currentValue === null ? (
                              <span style={{ color: "var(--color-text-3)" }}>{C.noCurrent}</span>
                            ) : (
                              <>
                                <div>
                                  {formatTargetNumber(t.progress.currentValue, {
                                    unit: t.unit,
                                    percent: isPct,
                                  })}
                                </div>
                                <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
                                  {t.progress.currentYear
                                    ? C.currentFrom(t.progress.currentYear)
                                    : ""}
                                </div>
                              </>
                            )}
                          </td>
                          <td
                            style={{
                              ...td,
                              textAlign: "right",
                              color: targetToneColor(tone),
                              fontWeight: 600,
                            }}
                          >
                            {formatDistance(t)}
                          </td>
                          <td style={td}>
                            <TargetProgressBar progress={t.progress} />
                          </td>
                          <td style={{ ...td, fontSize: "0.8rem", color: "var(--color-text-2)" }}>
                            {t.owner ?? "—"}
                          </td>
                          <td style={{ ...td, fontSize: "0.8rem" }}>
                            {KPI_TARGET_STATUS_LABELS[t.status]}
                          </td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>
                            {canEdit ? (
                              <div className="flex gap-2 items-center">
                                <button
                                  type="button"
                                  style={link}
                                  onClick={() => setEditing({ metricId: ind.metricId, target: t })}
                                >
                                  {C.edit}
                                </button>
                                <button
                                  type="button"
                                  style={{ ...link, color: "var(--color-error)" }}
                                  onClick={() => setConfirm(t)}
                                >
                                  {C.remove}
                                </button>
                                {idx === rows.length - 1 ? addBtn : null}
                              </div>
                            ) : null}
                          </td>
                        </tr>
                        {isEditing ? editorRow(ind, t) : null}
                      </Fragment>
                    );
                  })
                )}
                {editing?.metricId === ind.metricId && !editing.target ? editorRow(ind) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <ConfirmDialog
        open={confirm !== null}
        title={C.deleteTitle}
        body={
          confirm
            ? `${confirm.label} · ${formatTargetCell(confirm)} — ${C.deleteBody}`
            : C.deleteBody
        }
        confirmLabel={C.deleteConfirm}
        tone="danger"
        busy={busy}
        onConfirm={() => {
          if (!confirm) return;
          void onDelete(confirm.id).then(() => setConfirm(null));
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
